import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const IATA = z.string().regex(/^[A-Z]{3}$/);

export type MonthlyPricePoint = {
  /** YYYY-MM */
  month: string;
  /** Skrócona etykieta po polsku (sty, lut, mar…). */
  label: string;
  avg: number;
  min: number;
  samples: number;
};

const PL_MONTHS_SHORT = [
  "sty", "lut", "mar", "kwi", "maj", "cze",
  "lip", "sie", "wrz", "paź", "lis", "gru",
];

/**
 * Średnia/min cena per miesiąc kalendarzowy dla trasy — ostatnie 12 miesięcy.
 * Downsampling: per dzień bierzemy min display_price (cron może odpalać kilka
 * razy na dobę), potem per miesiąc liczymy avg z dziennych minimów.
 *
 * Cel: pokazać userowi "kiedy najtaniej do X" konkretnym wykresem, nie
 * heurystyką z `bestMonthForRoute`.
 */
export const getMonthlyPrices = createServerFn({ method: "GET" })
  .inputValidator((data: { from: string; to: string }) =>
    z.object({ from: IATA, to: IATA }).parse(data),
  )
  .handler(async ({ data }): Promise<MonthlyPricePoint[]> => {
    const supabaseUrl = process.env.SUPABASE_URL || process.env.VITE_SUPABASE_URL;
    const anonKey =
      process.env.SUPABASE_PUBLISHABLE_KEY || process.env.SUPABASE_ANON_KEY;
    if (!supabaseUrl || !anonKey) return [];

    const since = new Date(Date.now() - 365 * 86_400_000).toISOString();
    const url =
      `${supabaseUrl}/rest/v1/route_price_history` +
      `?from_iata=eq.${data.from}&to_iata=eq.${data.to}` +
      `&checked_at=gte.${encodeURIComponent(since)}` +
      `&select=checked_at,display_price&order=checked_at.asc&limit=5000`;

    const res = await fetch(url, {
      headers: { apikey: anonKey, Authorization: `Bearer ${anonKey}` },
    });
    if (!res.ok) return [];
    const rows = (await res.json()) as Array<{
      checked_at: string;
      display_price: number;
    }>;
    if (rows.length === 0) return [];

    // Min per dzień (żeby noisy cron nie zaburzał miesięcznej średniej).
    const byDay = new Map<string, number>();
    for (const r of rows) {
      if (!Number.isFinite(r.display_price) || r.display_price <= 0) continue;
      const day = r.checked_at.slice(0, 10);
      const cur = byDay.get(day);
      if (cur == null || r.display_price < cur) byDay.set(day, r.display_price);
    }

    // Agregacja per miesiąc.
    const byMonth = new Map<string, number[]>();
    for (const [day, price] of byDay) {
      const month = day.slice(0, 7); // YYYY-MM
      const arr = byMonth.get(month) ?? [];
      arr.push(price);
      byMonth.set(month, arr);
    }

    const out: MonthlyPricePoint[] = [];
    for (const [month, prices] of [...byMonth.entries()].sort()) {
      const sum = prices.reduce((s, v) => s + v, 0);
      const monthIdx = Number(month.slice(5, 7)) - 1;
      out.push({
        month,
        label: PL_MONTHS_SHORT[monthIdx] ?? month,
        avg: Math.round(sum / prices.length),
        min: Math.min(...prices),
        samples: prices.length,
      });
    }
    return out;
  });
